import clsx from 'clsx';
import { cloneElement, type ReactElement, type ReactNode } from 'react';
import type { IconBaseProps } from 'react-icons/lib';

import { formatLabelText } from '~utils';

/* -------------------------------------------------------------------------- */
/*                                    Type                                    */
/* -------------------------------------------------------------------------- */

type Variant = 'default' | 'featured';

type PropertyItemProps = {
  /** Label for the property value */
  label: string;
  /** Optional react-icons icon element to display alongside the label */
  icon?: ReactElement<IconBaseProps>;
  /** Value to display (string, number, or ReactNode) */
  value: string | number | ReactNode;
  /** Optional unit measure to append to numeric values (e.g., 'm', 'kg') */
  unit?: string;
  /** Controls the visual style of the label and value. Defaults to 'default'. */
  variant?: Variant;
  /** Optional flag to apply background and padding styles to the entire component for emphasis */
  contained?: boolean;
};

/* -------------------------------------------------------------------------- */
/*                                  Constants                                 */
/* -------------------------------------------------------------------------- */

const VARIANT_STYLES: Record<Variant, { label: string; value: string }> = {
  default: {
    label: 'text-xs text-muted-foreground uppercase',
    value: 'font-bold text-base text-foreground leading-tight',
  },
  featured: {
    label: 'text-xs text-muted-foreground uppercase',
    value: 'text-xl font-bold text-foreground leading-tight',
  },
};

/* -------------------------------------------------------------------------- */
/*                               Main component                               */
/* -------------------------------------------------------------------------- */

/**
 * Component to display a labeled property with an optional icon and unit,
 * supporting different visual variants and an optional contained style for emphasis.
 *
 * @example
 * ```tsx
 *   <PropertyItem
 *     label="Hardness"
 *     value={hole.hardness}
 *     unit="MPa"
 *     icon={<LuLayers2 />}
 *     variant="featured"
 *     contained
 *   />
 * ```
 */
const PropertyItem = ({ label, value, unit, icon, variant = 'default', contained }: PropertyItemProps) => {
  const styles = VARIANT_STYLES[variant];
  // If an icon is provided, set default size to 20 and ensure it's hidden from screen readers,
  // while allowing consumer-provided props to override these defaults if needed.
  const iconElement = icon ? cloneElement(icon, { size: 20, 'aria-hidden': true, ...icon.props }) : null;

  return (
    <dl className={clsx({ 'min-w-0 space-y-2': true, 'rounded-lg bg-muted p-3': contained })}>
      <dt className={clsx('flex items-center gap-1', styles.label)}>
        {iconElement}
        {formatLabelText(label)}
      </dt>
      <dd className={clsx(styles.value, 'wrap-break-word')}>
        {value}
        {unit && <span className="font-light text-muted-foreground/70 text-sm"> {unit}</span>}
      </dd>
    </dl>
  );
};
PropertyItem.displayName = 'PropertyItem';

/* -------------------------------------------------------------------------- */
/*                                   Exports                                  */
/* -------------------------------------------------------------------------- */

export { PropertyItem };
